import React from "react";
import { RefreshCw, OctagonAlert, ShieldAlert, Truck, PackageX, Globe, Box, Layers, AlertCircle, FileText, Sparkles } from "lucide-react";
import { WIZARD_TEMPLATES, SEVERITY_OPTIONS, PARTY_LAYER_COLOR } from "../../data/wizardTemplates";
import { severityColor } from "../../data/mockData";
import { SectionLabel } from "./wizardAtoms";
import { useIncidentTemplates } from "../../api/sitroom";

const ICON_MAP = {
  RefreshCw,
  OctagonAlert,
  ShieldAlert,
  TruckIcon: Truck,
  PackageX,
  Globe,
  BoxIcon: Box,
  Layers,
};

// Best-effort map saved Incident Template name → WIZARD_TEMPLATES.value.
const guessTemplateValue = (savedName = "") => {
  const n = savedName.toLowerCase();
  if (n.includes("jis") || n.includes("sequence")) return "jis";
  if (n.includes("stop") || n.includes("line")) return "stoppage";
  if (n.includes("quality") || n.includes("lock")) return "quality";
  if (n.includes("transport") || n.includes("delay") || n.includes("inbound")) return "transport";
  if (n.includes("packag")) return "packaging";
  if (n.includes("custom") || n.includes("border")) return "customs";
  if (n.includes("damage") || n.includes("warehouse") || n.includes("loss")) return "damage";
  if (n.includes("raw") || n.includes("material")) return "material";
  return "quality"; // safe default
};

export const WizardStep1Template = ({ form, setForm }) => {
  const selectedTpl = WIZARD_TEMPLATES.find((t) => t.value === form.template);
  const { data: savedTemplates = [] } = useIncidentTemplates();

  const [appliedTpl, setAppliedTpl] = React.useState(null);

  const applySavedTemplate = (savedKey) => {
    const tpl = savedTemplates.find((t) => t.template_key === savedKey);
    if (!tpl) { setAppliedTpl(null); return; }
    const wizardValue = guessTemplateValue(tpl.name);
    const extraParties = (tpl.auto_linked_party_types || []).map((pt) => ({
      name: pt, role: "From template", type: pt,
    }));
    setForm((f) => ({
      ...f,
      template: wizardValue,
      severity: tpl.default_severity || f.severity,
      additionalParties: extraParties,
    }));
    setAppliedTpl(tpl);
  };

  return (
    <div data-testid="wizard-step1">
      <h2
        style={{
          fontFamily: "Syne, sans-serif",
          fontSize: 22,
          fontWeight: 600,
          color: "#f0f2f8",
          marginBottom: 4,
          letterSpacing: "-0.3px",
        }}
      >
        Choose an incident template
      </h2>
      <p
        style={{
          fontFamily: "'DM Mono', monospace",
          fontSize: 11,
          color: "#9198b0",
          letterSpacing: "0.5px",
          marginBottom: 24,
        }}
      >
        Each template pre-fills severity and the parties that need to be in the room.
      </p>

      {/* Iter47-A — saved incident-template quick-start */}
      {savedTemplates.length > 0 && (
        <div
          className="mb-6 p-3 rounded-md"
          style={{ background: "rgba(24,95,165,0.08)", border: "1px solid rgba(24,95,165,0.30)" }}
          data-testid="wizard-saved-templates"
        >
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={14} color="#9ec3ec" />
            <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 13, color: "#9ec3ec" }}>
              Quick-start from saved template
            </span>
          </div>
          <select
            onChange={(e) => applySavedTemplate(e.target.value)}
            defaultValue=""
            style={{
              width: "100%",
              background: "#08090d",
              border: "1px solid rgba(255,255,255,0.10)",
              color: "#f0f2f8",
              fontFamily: "'DM Mono', monospace",
              fontSize: 11,
              padding: "8px 10px",
              borderRadius: 6,
              outline: "none",
            }}
          data-testid="wizard-saved-template-select"
          id="wizard-saved-template-select"
          >
            <option value="">— Pick a saved template to pre-fill severity, parties, mandatory fields —</option>
            {savedTemplates.map((t) => (
              <option key={t.template_key} value={t.template_key}>
                {t.name} · v{t.version} · {t.default_severity?.toUpperCase()} · {t.default_sla_tier}
              </option>
            ))}
          </select>
          {appliedTpl && (
            <div className="mt-3 p-2 rounded" style={{ background: "rgba(78,208,132,0.08)", border: "1px solid rgba(78,208,132,0.30)" }} data-testid="wizard-template-applied-banner">
              <div className="flex items-center gap-2 mb-1">
                <FileText size={11} color="#4ed084" />
                <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 12, color: "#4ed084" }}>
                  Template applied — {appliedTpl.name}
                </span>
              </div>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#c5cad8", letterSpacing: "0.3px", lineHeight: 1.5 }}>
                Severity <b style={{ color: "#9ec3ec" }}>{appliedTpl.default_severity?.toUpperCase()}</b>
                {" · "}SLA tier <b style={{ color: "#9ec3ec" }}>{appliedTpl.default_sla_tier}</b>
                {appliedTpl.auto_linked_party_types?.length > 0 && (
                  <> · auto-linked <b style={{ color: "#f3b14a" }}>{appliedTpl.auto_linked_party_types.join(", ")}</b></>
                )}
              </div>
              {appliedTpl.mandatory_fields?.length > 0 && (
                <div className="mt-1.5" style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#f3b14a", letterSpacing: "0.3px" }}>
                  ⚠ Mandatory fields: {appliedTpl.mandatory_fields.join(", ")} — please complete in Step 2.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}
      >
        {WIZARD_TEMPLATES.map((t) => {
          const Icon = ICON_MAP[t.icon] || RefreshCw;
          const selected = form.template === t.value;
          return (
            <button
              key={t.value}
              type="button"
              onClick={() => setForm((f) => ({ ...f, template: t.value, severity: t.sev }))}
              className="text-left transition-all"
              style={{
                background: selected ? "rgba(24,95,165,0.10)" : "#0d0f18",
                border: `1px solid ${selected ? "#185FA5" : "rgba(255,255,255,0.08)"}`,
                borderRadius: 8,
                padding: 14,
                cursor: "pointer",
              }}
              data-testid={`wizard-template-${t.value}`}
            >
              <div className="flex items-start justify-between mb-2">
                <Icon size={18} style={{ color: selected ? "#9ec3ec" : "#9198b0" }} />
                <span
                  className="px-2 py-0.5 rounded"
                  style={{
                    background: `${severityColor(t.sev)}24`,
                    border: `1px solid ${severityColor(t.sev)}66`,
                    color: severityColor(t.sev),
                    fontFamily: "'DM Mono', monospace",
                    fontSize: 9,
                    letterSpacing: "1.2px",
                    textTransform: "uppercase",
                  }}
                >
                  {t.sev}
                </span>
              </div>
              <div
                style={{
                  fontFamily: "Syne, sans-serif",
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#f0f2f8",
                  marginBottom: 4,
                }}
              >
                {t.label}
              </div>
              <div
                style={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 10,
                  color: "#9198b0",
                  marginBottom: 8,
                  letterSpacing: "0.3px",
                }}
              >
                {t.description}
              </div>
              <div className="flex flex-wrap gap-1">
                {t.parties.slice(0, 3).map((p) => (
                  <span
                    key={p}
                    className="px-1.5 py-0.5 rounded"
                    style={{
                      background: "rgba(255,255,255,0.04)",
                      color: PARTY_LAYER_COLOR[p] || "#9198b0",
                      fontFamily: "'DM Mono', monospace",
                      fontSize: 9,
                      letterSpacing: "0.5px",
                    }}
                  >
                    {p}
                  </span>
                ))}
              </div>
            </button>
          );
        })}
      </div>

      {selectedTpl && (
        <div className="mt-6">
          <SectionLabel>Severity Override</SectionLabel>
          <div className="flex flex-wrap gap-2">
            {SEVERITY_OPTIONS.map((s) => {
              const active = form.severity === s.value;
              return (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, severity: s.value }))}
                  className="px-3 py-1.5 rounded transition-colors"
                  style={{
                    background: active ? severityColor(s.value) : "rgba(255,255,255,0.04)",
                    border: `1px solid ${active ? severityColor(s.value) : "rgba(255,255,255,0.10)"}`,
                    color: active ? "#fff" : "#c5cad8",
                    fontFamily: "'DM Mono', monospace",
                    fontSize: 11,
                    letterSpacing: "1px",
                    textTransform: "uppercase",
                    cursor: "pointer",
                  }}
                  data-testid={`wizard-sev-${s.value}`}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {form.severity === "s1" && (
        <div
          className="mt-6 p-3 rounded-md flex items-start gap-2"
          style={{
            background: "rgba(226,75,74,0.10)",
            border: "1px solid rgba(226,75,74,0.45)",
          }}
          data-testid="wizard-s1-banner"
        >
          <AlertCircle size={16} style={{ color: "#E24B4A", marginTop: 1, flexShrink: 0 }} />
          <div
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 11,
              color: "#ff7e7c",
              letterSpacing: "0.3px",
              lineHeight: 1.55,
            }}
          >
            <strong style={{ color: "#E24B4A" }}>S1 Line Stop</strong> — SLA timer starts
            immediately on submit. All linked parties will be notified via email + SMS.
          </div>
        </div>
      )}
    </div>
  );
};
